/**
 * Retry interceptor — hide transient origin failures (Cloudflare 520-524,
 * upstream 502-504, network errors) from the user. The goal: a 30-second
 * hiccup in the backend never surfaces as a red error page during login
 * or normal page loads.
 *
 * Rules
 * -----
 * • Retries only these status codes: 502, 503, 504, 520, 521, 522, 523, 524
 *   plus true network errors (ERR_NETWORK, ECONNABORTED, timeout).
 * • Never retries 4xx — those are legit business errors (bad password,
 *   permission denied, validation) and MUST reach the caller unchanged.
 * • Safe-to-retry set: GET (idempotent), plus specific POST endpoints
 *   whose repetition on server is a no-op:
 *     - /api/auth/login          (server deduplicates via credentials)
 *     - /api/auth/refresh        (idempotent)
 *     - /api/auth/logout         (idempotent)
 *     - /api/health, /api/health/deep
 *   Any other POST/PATCH/PUT/DELETE fails after one attempt (the offline
 *   outbox handles the 4 whitelisted mutations separately).
 * • Exponential backoff: 500 ms, 1500 ms. Total worst-case: 2 s of extra
 *   latency before the user sees an error.
 * • Never more than 2 retries (3 attempts total) to avoid amplifying an
 *   incident.
 */

const RETRY_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const RETRY_POST_URLS = [
  /\/api\/auth\/login\b/,
  /\/api\/auth\/refresh\b/,
  /\/api\/auth\/logout\b/,
  /\/api\/health(\/|$)/,
];
const MAX_RETRIES = 2;
const BACKOFF_MS = [500, 1500];

function shouldRetry(cfg, error) {
  if (!cfg) return false;
  const method = String(cfg.method || 'get').toUpperCase();
  const url = cfg.url || '';
  const status = error?.response?.status;

  // Only retry on the whitelisted transient failure modes.
  const isTransient = (
    status !== undefined && RETRY_STATUS.has(status)
  ) || (
    !error?.response && (
      error?.code === 'ERR_NETWORK' ||
      error?.code === 'ECONNABORTED' ||
      /network|timeout/i.test(error?.message || '')
    )
  );
  if (!isTransient) return false;

  if (method === 'GET') return true;
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    return RETRY_POST_URLS.some((rx) => rx.test(url));
  }
  return false;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function installRetryInterceptor(axios) {
  if (axios.__tovias_retry_installed) return;
  axios.__tovias_retry_installed = true;

  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error?.config;
      if (!cfg) return Promise.reject(error);
      cfg.__retryCount = cfg.__retryCount || 0;
      if (cfg.__retryCount >= MAX_RETRIES) return Promise.reject(error);
      if (!shouldRetry(cfg, error)) return Promise.reject(error);

      const wait = BACKOFF_MS[cfg.__retryCount] ?? 1500;
      cfg.__retryCount += 1;
      // eslint-disable-next-line no-console
      console.info(
        `[tovias-retry] ${cfg.method?.toUpperCase()} ${cfg.url} → attempt ${cfg.__retryCount + 1} after ${wait} ms`,
      );
      await sleep(wait);
      return axios(cfg);
    },
  );
}
